import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import {
  ClipboardCheck,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Settings,
  ArrowLeft,
  AlertTriangle,
  Users,
  Target,
  TrendingUp,
  FileText,
  Loader2
} from 'lucide-react';

/**
 * Assessments Admin Preview Page
 * 
 * Hidden preview for Super Admins to test assessment functionality
 * before public release. Shows actual module UI scaffolding.
 */
export default function AssessmentsAdminPreviewPage() {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [templates, setTemplates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/assessments');
      return;
    }
    loadData();
  }, [isSuperAdmin]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, summaryRes] = await Promise.all([
        apiService.getAssessmentTemplates({}),
        apiService.getAssessmentSummary()
      ]);
      setTemplates(templatesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to load assessment data:', error);
      // Expected to fail if module not fully enabled
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'templates', label: 'Templates', icon: FileText },
    { id: 'administrations', label: 'Administrations', icon: ClipboardCheck },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const stats = [
    { label: 'Templates', value: templates.length, icon: FileText, color: 'primary' },
    { label: 'Administrations', value: summary?.total_administrations || 0, icon: ClipboardCheck, color: 'info' },
    { label: 'Students Assessed', value: summary?.students_assessed || 0, icon: Users, color: 'success' },
    { label: 'At Risk', value: summary?.at_risk_count || 0, icon: AlertTriangle, color: 'danger' }
  ];

  return (
    <div className="page-container" data-testid="assessments-admin-preview-page">
      {/* Admin Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
        <AlertTriangle size={20} className="text-amber-600 flex-shrink-0" />
        <div>
          <div className="font-medium text-amber-800">Super Admin Preview Mode</div>
          <div className="text-sm text-amber-700">
            This is a preview of the Assessments module. It is not visible to regular users.
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-[#595959] hover:text-[#2C4C3B] mb-2 transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
          <h1 className="page-title">Assessments</h1>
          <p className="page-subtitle">Admin Preview - Screen students and connect results to interventions</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => toast.info('Template builder coming in Phase 2')}
          data-testid="create-template-btn"
        >
          <Plus size={18} />
          Create Template
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs-container mb-6">
        <div className="tabs-list">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-item flex items-center gap-2 ${activeTab === tab.id ? 'active' : ''}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-[#2C4C3B]" />
        </div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div>
              {/* Stats Grid */}
              <div className="stats-grid mb-8">
                {stats.map((stat, index) => (
                  <div key={index} className="stat-card">
                    <div className={`stat-icon ${stat.color}`}>
                      <stat.icon size={24} />
                    </div>
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#2C4C3B]/10 rounded-xl flex items-center justify-center">
                      <ClipboardCheck size={24} className="text-[#2C4C3B]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1C1C1C]">Administer Assessment</h3>
                      <p className="text-sm text-[#595959]">Record a new assessment</p>
                    </div>
                  </div>
                  <button 
                    className="btn btn-secondary w-full"
                    onClick={() => toast.info('Administration flow coming in Phase 2')}
                  >
                    Start Assessment
                  </button>
                </div>

                <div className="card p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#075985]/10 rounded-xl flex items-center justify-center">
                      <Target size={24} className="text-[#075985]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1C1C1C]">Create Intervention</h3>
                      <p className="text-sm text-[#595959]">From assessment results</p>
                    </div>
                  </div>
                  <button 
                    className="btn btn-secondary w-full"
                    onClick={() => toast.info('Intervention workflow coming in Phase 3')}
                  >
                    View Results
                  </button>
                </div>

                <div className="card p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#166534]/10 rounded-xl flex items-center justify-center">
                      <TrendingUp size={24} className="text-[#166534]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1C1C1C]">Progress Reports</h3>
                      <p className="text-sm text-[#595959]">View assessment trends</p>
                    </div>
                  </div>
                  <button 
                    className="btn btn-secondary w-full"
                    onClick={() => toast.info('Reports coming in Phase 3')}
                  >
                    View Reports
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div>
              {/* Search & Filter */}
              <div className="filters-bar mb-6">
                <div className="search-container flex-1 max-w-md">
                  <Search size={18} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search templates..."
                    className="search-input"
                  />
                </div>
                <select className="filter-select">
                  <option value="">All Categories</option>
                  <option value="screener">Screeners</option>
                  <option value="skill_probe">Skill Probes</option>
                  <option value="follow_up">Follow-Up</option>
                </select>
                <select className="filter-select">
                  <option value="">All Domains</option>
                  <option value="academic">Academic</option>
                  <option value="behavior_sel">Behavior/SEL</option>
                  <option value="communication">Communication</option>
                </select>
              </div>

              {templates.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <FileText size={32} />
                  </div>
                  <h3 className="empty-title">No templates yet</h3>
                  <p className="empty-text">
                    Create your first assessment template to get started.
                  </p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => toast.info('Template builder coming soon')}
                  >
                    <Plus size={18} />
                    Create Template
                  </button>
                </div>
              ) : (
                <div className="card">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Domain</th>
                        <th>Grades</th>
                        <th>Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map(template => (
                        <tr key={template.id}>
                          <td className="font-medium">{template.name}</td>
                          <td>
                            <span className="badge badge-tier1">
                              {template.category}
                            </span>
                          </td>
                          <td>{template.domain}</td>
                          <td>{template.grade_min} - {template.grade_max}</td>
                          <td>
                            <span className={`badge ${template.status === 'active' ? 'badge-improving' : 'badge-new'}`}>
                              {template.status}
                            </span>
                          </td>
                          <td>
                            <button className="btn btn-ghost btn-icon">
                              <MoreVertical size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Administrations Tab */}
          {activeTab === 'administrations' && (
            <div className="empty-state">
              <div className="empty-icon">
                <ClipboardCheck size={32} />
              </div>
              <h3 className="empty-title">No assessments recorded</h3>
              <p className="empty-text">
                Assessment administrations will appear here once templates are created.
              </p>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4">Module Settings</h3>
              <p className="text-sm text-[#595959] mb-6">
                Configure assessment module behavior and permissions.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-[#E5E5E5]">
                  <div>
                    <div className="font-medium">Enable Module for All Users</div>
                    <div className="text-sm text-[#595959]">Make assessments available to all school users</div>
                  </div>
                  <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                    Coming Soon
                  </div>
                </div>
                
                <div className="flex items-center justify-between py-3 border-b border-[#E5E5E5]">
                  <div>
                    <div className="font-medium">Pilot Mode</div>
                    <div className="text-sm text-[#595959]">Enable for selected schools only</div>
                  </div>
                  <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                    Coming Soon
                  </div>
                </div>
                
                <div className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium">Auto-Intervention Recommendations</div>
                    <div className="text-sm text-[#595959]">Automatically suggest interventions from results</div>
                  </div>
                  <div className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
                    Phase 3
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
